import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Clock, BookOpen } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { StatusBadge } from './Insider';
import PosterPlaceholder from './PosterPlaceholder';
import SiteHeader from './SiteHeader';
import WatchlistButton from './WatchlistButton';
import ProjectFinance from './ProjectFinance';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// PROJECT_GREENLIT -> "Project Greenlit"
function formatEventType(type) {
  return type
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function formatDate(value) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return value;
  }
}

function Timeline({ events }) {
  if (!events.length) return <p className="text-sm text-gray-400">No events recorded yet.</p>;
  return (
    <ol className="relative border-l border-gray-200 ml-2">
      {events.map((e) => (
        <li key={e.id} className="mb-6 ml-4">
          <div className="absolute w-3 h-3 bg-gray-300 rounded-full -left-1.5 mt-1.5" />
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-900">{formatEventType(e.event_type)}</span>
            {e.event_date && <span className="text-xs text-gray-400">{formatDate(e.event_date)}</span>}
            {typeof e.confidence === 'number' && e.confidence < 1 && (
              <span className="text-[11px] text-amber-600">{Math.round(e.confidence * 100)}% confidence</span>
            )}
          </div>
          {e.description && <p className="text-sm text-gray-600 mt-0.5">{e.description}</p>}
          {e.source_name && (
            <p className="text-xs text-gray-400 mt-1 inline-flex items-center gap-1">
              {e.source_url ? (
                <a href={e.source_url} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1 hover:text-gray-600">
                  {e.source_name} <ExternalLink className="w-3 h-3" />
                </a>
              ) : e.source_name}
            </p>
          )}
        </li>
      ))}
    </ol>
  );
}

function PeopleList({ people }) {
  if (!people.length) return null;
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-900 mb-2">People</h3>
      <ul className="space-y-1">
        {people.map((p) => (
          <li key={p.id} className="text-sm text-gray-700">
            <Link to={p.tmdb_id ? `/person/${p.tmdb_id}` : `/?q=${encodeURIComponent(p.name)}`}
                  className="font-medium text-videbits-700 hover:text-videbits-900 hover:underline">
              {p.name}
            </Link>
            <span className="text-gray-400"> — {p.role}{p.character_name ? ` (${p.character_name})` : ''}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Graph person-roles that denote source material a project is adapted from,
// mapped to a display label. Kept in sync with the extraction + graph CREW_ROLES.
const SOURCE_ROLES = {
  'Novel': 'novel',
  'Short Story': 'short story',
  'Book': 'book',
  'Graphic Novel': 'graphic novel',
  'Comic Book': 'comic book',
  'Play': 'play',
  'Author': 'book',
};

function BasedOn({ people }) {
  const sources = people.filter((p) => SOURCE_ROLES[p.role]);
  if (!sources.length) return null;
  const byType = {};
  sources.forEach((p) => {
    const t = SOURCE_ROLES[p.role];
    (byType[t] = byType[t] || []).push(p);
  });
  const groups = Object.entries(byType);
  return (
    <p className="inline-flex items-start gap-2 text-sm text-gray-600 bg-videbits-50 border border-videbits-100 rounded-lg px-3 py-2 mt-3">
      <BookOpen className="w-4 h-4 text-videbits-600 mt-0.5 flex-shrink-0" />
      <span>
        Based on{' '}
        {groups.map(([type, authors], gi) => (
          <React.Fragment key={type}>
            the {type} by{' '}
            {authors.map((a, ai) => (
              <React.Fragment key={a.id}>
                {ai > 0 && (ai === authors.length - 1 ? ' and ' : ', ')}
                <Link
                  to={a.tmdb_id ? `/person/${a.tmdb_id}` : `/?q=${encodeURIComponent(a.name)}`}
                  className="font-medium text-videbits-700 hover:text-videbits-900 hover:underline"
                >
                  {a.name}
                </Link>
              </React.Fragment>
            ))}
            {gi < groups.length - 1 ? '; ' : ''}
          </React.Fragment>
        ))}
      </span>
    </p>
  );
}

function CompaniesList({ companies }) {
  if (!companies.length) return null;
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-900 mb-2">Companies</h3>
      <ul className="space-y-1">
        {companies.map((c) => (
          <li key={c.id} className="text-sm text-gray-700">
            <Link to={`/company/${c.id}`}
                  className="font-medium text-videbits-700 hover:text-videbits-900 hover:underline">
              {c.name}
            </Link>
            <span className="text-gray-400"> — {c.role}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SourcesList({ sources }) {
  if (!sources.length) return null;
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-900 mb-2">Sources</h3>
      <ul className="space-y-1">
        {sources.map((s) => (
          <li key={s.id} className="text-sm">
            {s.url ? (
              <a href={s.url} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-900">
                {s.name} <ExternalLink className="w-3 h-3" />
              </a>
            ) : <span className="text-gray-700">{s.name}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function InsiderProject() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const resp = await fetch(`${API_BASE}/api/insider/projects/${id}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (resp.status === 404) { setNotFound(true); return; }
      if (!resp.ok) throw new Error(`Request failed (${resp.status})`);
      const data = await resp.json();
      // If this title exists in the catalogue (has a TMDb id), the unified title
      // page is the public movie/TV page — go there instead of the Insider page.
      if (data.tmdb_id) {
        navigate(`/${data.media_type === 'tv' ? 'tv' : 'movie'}/${data.tmdb_id}`, { replace: true });
        return;
      }
      setProject(data);
    } catch {
      setNotFound(true);
    } finally {
      setIsLoading(false);
    }
  }, [id, token, navigate]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />
      <div className="max-w-6xl mx-auto px-4 pt-4">
        <Link to="/insider" className="inline-flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900">
          <ArrowLeft className="w-4 h-4" /> Back to Radar
        </Link>
      </div>

      {isLoading ? (
        <div className="max-w-6xl mx-auto px-4 py-16 text-center text-gray-400">
          <Clock className="w-6 h-6 mx-auto mb-2 animate-pulse" /> Loading…
        </div>
      ) : notFound ? (
        <div className="max-w-6xl mx-auto px-4 py-16 text-center">
          <p className="text-gray-500">Project not found.</p>
          <Link to="/insider" className="text-sm text-blue-600 hover:underline mt-2 inline-block">Back to the radar</Link>
        </div>
      ) : project && (
        <div className="max-w-6xl mx-auto px-4 py-10">
          <div className="flex gap-6 mb-10">
            <div className="flex-shrink-0 w-36 h-52 rounded-xl overflow-hidden bg-gray-100 shadow-sm">
              {project.poster_url ? (
                <img src={project.poster_url} alt={project.title} className="w-full h-full object-cover" />
              ) : (
                <PosterPlaceholder />
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap mb-2">
                <h2 className="text-2xl font-semibold text-gray-900">{project.title}</h2>
                <StatusBadge status={project.status} />
                <span className="text-xs uppercase tracking-wide text-gray-400">{project.media_type}</span>
                <WatchlistButton entityType="title" entityId={project.id} />
              </div>
              {project.overview && <p className="text-sm text-gray-600 leading-relaxed max-w-2xl">{project.overview}</p>}
              <BasedOn people={project.people || []} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div className="md:col-span-2">
              {project.finance && <div className="mb-8"><ProjectFinance finance={project.finance} /></div>}
              <h3 className="text-lg font-semibold text-gray-900 mb-5">Timeline</h3>
              <Timeline events={project.timeline || []} />
            </div>
            <div className="space-y-8">
              <PeopleList people={(project.people || []).filter((p) => !SOURCE_ROLES[p.role])} />
              <CompaniesList companies={project.companies || []} />
              <SourcesList sources={project.sources || []} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
